// components/OgpPreview.js
import React, { useState, useEffect, useMemo, memo } from 'react';
import useOgpCache from '../hooks/useOgpCache';

const OgpPreview = memo(({ url, preloadedData }) => {
  const [ogpData, setOgpData] = useState(preloadedData || null);
  const [loading, setLoading] = useState(!preloadedData);
  const { fetchOgpData } = useOgpCache();

  const loadOgpData = useMemo(() => {
    return async () => {
      if (preloadedData) {
        setOgpData(preloadedData);
        setLoading(false);
        return;
      }

      try {
        const data = await fetchOgpData(url);
        setOgpData(data);
      } catch (error) {
        console.error('Error fetching OGP data:', error);
      } finally {
        setLoading(false);
      }
    };
  }, [url, preloadedData, fetchOgpData]);

  useEffect(() => {
    loadOgpData();
  }, [loadOgpData]);

  const loadingComponent = useMemo(() => (
    <div className="animate-pulse">
      <div className="aspect-[1200/630] bg-gray-200 rounded-t-lg"></div>
      <div className="p-6">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
  ), []);

  const ogpContent = useMemo(() => {
    if (!ogpData) return null;

    return (
      <>
        {ogpData.image && (
          <div className="aspect-[1200/630] overflow-hidden">
            <img
              src={ogpData.image}
              alt={ogpData.title || ''}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {ogpData.title}
              </h3>
              <p className="text-gray-600">{ogpData.description}</p>
              <p className="text-sm text-gray-500 mt-2">{ogpData.siteName}</p>
            </div>
            <span className="text-blue-600 ml-4">→</span>
          </div>
        </div>
      </>
    );
  }, [ogpData]);

  if (loading) {
    return loadingComponent;
  }

  return ogpContent;
});

OgpPreview.displayName = 'OgpPreview';

export default OgpPreview;